/**
 * Shared job-progress presentation rules for AnalysisProgress and
 * TranscriptionProgress (async reliability lane, Target design §e).
 *
 * The server decides that a job failed (job row, failure signal, reaper) and
 * always sends one terminal event. Client timers only change what we SHOW:
 * they never silently give up on a job that may still be running (that was
 * the ASYNC-6 double-charge trigger).
 */
import { formatTrialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';

/** Queued/preparing this long: "still waiting in line" notice. */
export const JOB_WAITING_IN_LINE_MS = 10 * 60_000;
/** Total time before "we lost track of this job". */
export const JOB_LOST_TRACK_TOTAL_MS = 35 * 60_000;
/** No status response at all for this long: "we lost track". */
export const JOB_STATUS_SILENCE_MS = 5 * 60_000;

export type JobStaleNotice = 'waitingInLine' | 'lostTrack' | null;

export interface JobStaleInput {
  now: number;
  startedAt: number;
  lastStatusAt: number;
  /** Best progress seen so far (0-100). */
  bestProgress: number;
  /** User pressed "Keep waiting" on the waiting-in-line notice. */
  waitingDismissed?: boolean;
  /** User pressed "Check again" at this time (restarts the lost-track clock). */
  lostTrackResetAt?: number | null;
}

/**
 * Which notice (if any) to show. Never implies failure: the job keeps being
 * polled and the user chooses to keep waiting, check again, or cancel.
 */
export function jobStaleNotice(input: JobStaleInput): JobStaleNotice {
  const { now, startedAt, lastStatusAt, bestProgress, waitingDismissed, lostTrackResetAt } = input;
  const totalAnchor = Math.max(startedAt, lostTrackResetAt ?? 0);
  if (now - lastStatusAt >= JOB_STATUS_SILENCE_MS || now - totalAnchor >= JOB_LOST_TRACK_TOTAL_MS) {
    return 'lostTrack';
  }
  if (!waitingDismissed && bestProgress <= 5 && now - startedAt >= JOB_WAITING_IN_LINE_MS) {
    return 'waitingInLine';
  }
  return null;
}

/** Error codes the backend can return (background_jobs.ERROR_*). */
export const JOB_ERROR_CODES = [
  'analysis_failed',
  'transcription_failed',
  'worker_lost',
  'timed_out',
  'not_started',
  'cancelled',
  'no_credits',
  'provider_unavailable',
  'dispatch_failed',
  'parent_failed',
  'task_failed',
  'invalid_audio',
  'trial_analysis_used',
] as const;

export type JobErrorCode = (typeof JOB_ERROR_CODES)[number];

type Translate = (key: string, fallbackOrOptions?: any, options?: any) => string;

export interface JobErrorPayload {
  error_code?: string | null;
  /** `jobs.errors.<code>`; used when the code itself is missing. */
  error_message_key?: string | null;
  error?: string | null;
  message?: string | null;
}

const JOB_ERROR_KEY_PREFIX = 'jobs.errors.';

/** Optional viewer context for codes whose copy needs it (trial plan start). */
export interface JobErrorContext {
  language?: string;
  planStartsAt?: string | null;
}

/** The known job error code carried by a payload, if any. */
export function jobErrorCode(payload: JobErrorPayload | null | undefined): JobErrorCode | null {
  const key = payload?.error_message_key || '';
  const code = payload?.error_code
    || (key.startsWith(JOB_ERROR_KEY_PREFIX) ? key.slice(JOB_ERROR_KEY_PREFIX.length) : '');
  return (JOB_ERROR_CODES as readonly string[]).includes(code) ? (code as JobErrorCode) : null;
}

/**
 * Localized, user-safe error text for a terminal job. Prefers the stable
 * error code (translated), then the server's static message, then fallback.
 */
export function localizedJobError(
  t: Translate,
  payload: JobErrorPayload | null | undefined,
  fallback: string,
  context?: JobErrorContext,
): string {
  const code = jobErrorCode(payload);
  if (code === 'trial_analysis_used' && context?.language) {
    // Same copy as the HTTP block, with the plan start date when known.
    return formatTrialAnalysisUsedMessage(t, context.language, context.planStartsAt);
  }
  if (code) {
    const translated = t(`jobs.errors.${code}`);
    if (translated && translated !== `jobs.errors.${code}`) {
      return translated;
    }
  }
  return payload?.error || payload?.message || fallback;
}
